// Supported locales for the platform i18n system.
//
// Codes are region-qualified BCP-47, consistent with GATSBY_LANG (en-US, es-LA).
// Adding a language = add an entry here + its catalog files under ./locales/<code>.
// Nothing else (steps/components) needs to change.

export type LocaleDef = {
  code: string // BCP-47, e.g. 'en-US'
  base: string // language subtag, e.g. 'en' — used for the fallback chain
  label: string // human label (for pickers/tooling)
}

// Labels stay unqualified while there is a single variant per language. If a
// second variant of the same language is ever added (e.g. es-ES alongside
// es-LA), qualify both then.
export const LOCALES = [
  { code: 'en-US', base: 'en', label: 'English' },
  { code: 'es-LA', base: 'es', label: 'Español' },
] as const satisfies readonly LocaleDef[]

/**
 * Every supported locale code. Adding an entry to LOCALES widens this union,
 * which makes `resources` in resources.ts fail to typecheck until that locale
 * gets its catalogs — a language can never be half-configured.
 */
export type LocaleCode = (typeof LOCALES)[number]['code']

export const DEFAULT_LOCALE = 'en-US'

export const SUPPORTED_CODES = LOCALES.map(l => l.code)

// i18next fallbackLng: regional → base language → default.
// e.g. a missing 'es-LA' key falls back to 'es', then 'en-US'.
export const FALLBACK_LNG: Record<string, string[]> = LOCALES.reduce(
  (acc, l) => {
    const chain = [l.base, DEFAULT_LOCALE].filter(
      (c, i, arr) => c !== l.code && arr.indexOf(c) === i,
    )
    acc[l.code] = chain
    return acc
  },
  { default: [DEFAULT_LOCALE] } as Record<string, string[]>,
)
